import { useState } from 'react'
import { useTabVisit } from './useTabVisit'

// Freezes a value the first time `ready` is true, then returns the frozen
// snapshot for the component's lifetime — inside a kept-alive tab, for the
// visit, so the next visit picks up what changed (as a remount used to).
// Useful for lists that should not reorder or shrink in response to user
// mutations mid-session.
//
// Pass `resetKey` (e.g. `user?.uid`) to clear the frozen snapshot when the
// key changes — prevents stale personalized data surviving auth transitions.
//
// The freeze happens during render rather than in an effect, so the snapshot
// belongs to the same render that produced the value. A render React later
// discards (e.g. a child calling setState during render, as
// useBatchedSubscriptions does on reset) discards this update with it and
// re-runs both — an effect-based freeze would instead commit the stale capture.
export function useFrozenValue<T>(value: T, ready: boolean, resetKey?: string | null): T {
  const key = `${useTabVisit()}|${resetKey}`
  const [frozen, setFrozen] = useState<{ v: T } | null>(null)
  const [prevKey, setPrevKey] = useState(key)

  if (prevKey !== key) {
    setPrevKey(key)
    setFrozen(null)
    return value
  }

  if (ready && frozen === null) {
    setFrozen({ v: value })
    return value
  }

  return frozen !== null ? frozen.v : value
}
